import React from "react";
import { type NextPage } from "next";
import { useRouter } from "next/router";
import { Flex, SimpleGrid, Text, Box, AspectRatio, type FlexProps } from "@chakra-ui/react";
import {
  getLandingPageProps,
  getPageMetaImage,
  type LandingPageProps,
  desktopOnlyDisplay,
  mobileOnlyDisplay,
} from "utils";
import { Page } from "components/page";
import { Section } from "components/section";
import { DecorativeBackgroundImage, FillImage } from "components/images";
import { HubSpotForm, type FormProps } from "components/hubspot";
import { ResourceCard } from "components/resource-card";
import { useHeaderHeight } from "contexts/header-height";
import { trackNewsletterSignup } from "components/event-tracking";
import { handleClientError } from "lib/errors";
import { Checklist, ChecklistItem } from "ui/checklist";
import { horizontalContainerPadding, negativeHorizontalContainerPadding } from "ui/container";
import bgImage from "../../public/assets/lifecycle-leaders/background.png";

const LifecycleLeaders: NextPage<LandingPageProps> = ({ page, announcementBanner, blogs }) => {
  const router = useRouter();

  const onFormSubmit = (data: Record<string, string>) => {
    try {
      trackNewsletterSignup("Lifecycle Leaders", data);
      void router.push("/lifecycle-leaders-confirmed");
    } catch (error) {
      handleClientError(`Error submitting lifecycle leaders form: ${error}`);
    }
  };

  return (
    <Page
      title={page.metaTitle}
      description={page.metaDescription}
      image={getPageMetaImage({ src: page.metaImageUrl, alt: page.metaTitle })}
      footerOptions={{
        hideCta: true,
      }}
    >
      <Section fullWidth noContainerPx my="0">
        <SimpleGrid columns={{ base: 1, md: 2 }} minH="80vh" alignItems="stretch">
          <Box
            my="0"
            mt={{ base: "small", sm: "bigger", md: "biggest" }}
            mb={{ base: "huge", md: "huger" }}
            maxW={{ base: "100%", md: "calc( (var(--chakra-sizes-container-section) / 2))" }}
            ml={{ base: "0", md: "auto" }}
            pl={horizontalContainerPadding}
            pr={{ base: "containerPx.mobile", lg: "biggest", xl: "huge" }}
          >
            <Box as="section">
              <Text as="h1" textStyle={{ base: "headline1", lg: "display3" }}>
                {page.title}
              </Text>
              <Text as="h2" textStyle="caption1" mt="small">
                {page.description}
              </Text>

              <FormSection
                display={mobileOnlyDisplay("flex")}
                mt="bigger"
                mx={negativeHorizontalContainerPadding}
                onSubmit={onFormSubmit}
              />

              <Text as="h2" mt={{ base: "bigger", sm: "biggest" }} mb="small" textStyle="headline3" fontSize="brand.md">
                What you&lsquo;ll get:
              </Text>

              <Checklist textStyle="caption1">
                <ChecklistItem>Customer journey breakdowns from the best brands in the world</ChecklistItem>
                <ChecklistItem>Exclusive content from leading lifecycle marketing practitioners</ChecklistItem>
              </Checklist>

              {blogs && (
                <>
                  <Text
                    as="h2"
                    mt={{ base: "bigger", sm: "biggest" }}
                    mb="big"
                    textStyle="headline3"
                    fontSize="brand.md"
                  >
                    Past Lifecycle Leaders content
                  </Text>

                  <SimpleGrid as="ul" gap="small" columns={{ base: 1, sm: 2 }} listStyleType="none">
                    {blogs.map((blog) => (
                      <Box key={blog.slug} as="li" boxSize="100%">
                        <ResourceCard
                          height="100%"
                          href={`/blog/${blog.slug}`}
                          minH="auto"
                          title={blog.title}
                          titleAs="h3"
                          titleProps={{ textStyle: "caption1", fontWeight: "bold" }}
                          image={
                            <AspectRatio ratio={2 / 1} w="100%">
                              <FillImage
                                priority
                                src={blog.imageUrl}
                                alt={blog.imageAlt ?? blog.title}
                                placeholder="empty"
                              />
                            </AspectRatio>
                          }
                        />
                      </Box>
                    ))}
                  </SimpleGrid>
                </>
              )}
            </Box>
          </Box>

          <FormSection display={desktopOnlyDisplay("flex")} onSubmit={onFormSubmit} />
        </SimpleGrid>
      </Section>
    </Page>
  );
};

type FormSectionProps = {
  onSubmit: FormProps["onSubmit"];
} & Omit<FlexProps, "onSubmit">;

function FormSection({ onSubmit, ...props }: FormSectionProps) {
  const { headerHeight } = useHeaderHeight();

  return (
    <Flex flexDir="column" align="center" justify="start" pos="relative" p={{ base: "small", md: "big" }} {...props}>
      <Box boxSize="100%" pos="absolute" inset={0}>
        <DecorativeBackgroundImage priority src={bgImage} placeholder="blur" />
      </Box>

      <Flex
        my={{ base: "small", md: "huger" }}
        pos={{ base: "relative", md: "sticky" }}
        top={{ base: "auto", md: `calc(${headerHeight}px + var(--chakra-space-huger))` }}
      >
        <Box
          borderRadius="base"
          bg="brand.background.light"
          boxShadow="md"
          p={{ base: "small", md: "bigger" }}
          w={{ base: "auto", lg: "md" }}
          maxW="100%"
        >
          <Text as="h2" textStyle="headline3" fontSize="brand.md" mb="small">
            Don&lsquo;t miss the next breakdown
          </Text>

          <HubSpotForm
            allowGmail
            label="Subscribe to increase LTV"
            formId="064c1dce-b838-45fd-a4a2-d5c9be6c0b79"
            onSubmit={onSubmit}
          />
        </Box>
      </Flex>
    </Flex>
  );
}

export const getStaticProps = getLandingPageProps("/lifecycle-leaders", {
  loadBlogs: { slugs: ["netflix-winback-emails", "true-classic-journey-breakdown"], allowHidden: true },
});

export default LifecycleLeaders;
